import type {
  InviteeRequest,
  InviteeResult,
  StoryInviteeRead,
} from "./storyInvitees.types";

/**
 * Offline fixtures for the "Invite members" tab of ShareStoryModal, shaped
 * exactly like `GET/POST /api/v2/stories/:storyId/invitees` so switching to
 * the live endpoint needs no call-site change.
 *
 * Scenarios (REACT_APP_STORY_INVITEES_SCENARIO):
 *   default — one accepted member, one still-pending email invite
 *   empty   — nobody invited yet
 *   error   — both endpoints fail, to exercise the error surfaces
 */
export type StoryInviteesScenario = "default" | "empty" | "error";

/** Real network is never instant; without this the loading states are invisible. */
export const MOCK_STORY_INVITEES_LATENCY_MS = 400;

const MOCK_INVITED_BY = "11111111-1111-4111-8111-000000000001";

export const MOCK_STORY_INVITEES: StoryInviteeRead[] = [
  {
    id: "22222222-2222-4222-8222-000000000001",
    story_id: "story-mock",
    invited_by: MOCK_INVITED_BY,
    user_id: "11111111-1111-4111-8111-000000000002",
    invite_email: null,
    email: "nancy.thomas@company.com",
    first_name: "Nancy",
    last_name: "Thomas",
    role: "editor",
    status: "accepted",
    error_message: null,
    token_expires_at: null,
    accepted_at: "2026-08-20T10:00:00Z",
    created_at: "2026-08-18T10:00:00Z",
  },
  {
    id: "22222222-2222-4222-8222-000000000002",
    story_id: "story-mock",
    invited_by: MOCK_INVITED_BY,
    user_id: null,
    invite_email: "barry@gmail.com",
    email: "barry@gmail.com",
    first_name: null,
    last_name: null,
    role: "viewer",
    status: "pending",
    error_message: null,
    token_expires_at: "2026-09-24T10:00:00Z",
    accepted_at: null,
    created_at: "2026-09-09T10:00:00Z",
  },
];

/** Mirrors the resolution rules in the OpenAPI description closely enough for local dev. */
export const buildMockInviteeResults = (
  invitees: InviteeRequest[],
): InviteeResult[] =>
  invitees.map((invitee, index) => {
    if ("user_id" in invitee && invitee.user_id) {
      const alreadyMember = MOCK_STORY_INVITEES.some(
        (existing) => existing.user_id === invitee.user_id,
      );
      return {
        user_id: invitee.user_id,
        email: MOCK_STORY_INVITEES.find((m) => m.user_id === invitee.user_id)
          ?.email ?? "unknown@company.com",
        role: invitee.role,
        status: alreadyMember ? "already_member" : "accepted",
        story_invitee_id: null,
        error_message: null,
      };
    }
    return {
      user_id: null,
      email: invitee.email ?? "",
      role: invitee.role,
      status: "pending",
      story_invitee_id: `22222222-2222-4222-8222-${String(index).padStart(12, "0")}`,
      error_message: null,
    };
  });
